/**
 * @fileoverview Month Selector Component
 * @description Pill-style month navigation with left/right arrows and a
 *              dropdown for quick-jumping. Styled to match the dashboard theme.
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import './MonthSelector.css';

/**
 * Format "YYYY-MM" → "Apr 2026"
 */
const formatShort = (monthStr) => {
  try {
    const [y, m] = monthStr.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return monthStr;
  }
};

/**
 * Format "YYYY-MM" → "April 2026"
 */
const formatLong = (monthStr) => {
  try {
    const [y, m] = monthStr.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return monthStr;
  }
};

/**
 * MonthSelector — pill-style navigation with prev/next arrows and a
 * dropdown for quick-jumping between available months.
 */
const MonthSelector = ({ months = [], selected, onChange, disabled = false }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const idx = months.indexOf(selected);
  const hasPrev = idx >= 0 && idx < months.length - 1;   // months are desc
  const hasNext = idx > 0;

  const goPrev = () => {
    if (hasPrev && !disabled) onChange(months[idx + 1]);
  };
  const goNext = () => {
    if (hasNext && !disabled) onChange(months[idx - 1]);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!months.length) return null;

  return (
    <div className={`ms-container ${disabled ? 'ms-disabled' : ''}`} ref={containerRef}>
      {/* Prev arrow */}
      <button
        className="ms-arrow"
        onClick={goPrev}
        disabled={!hasPrev || disabled}
        title="Previous month"
        aria-label="Previous month"
      >
        <ChevronLeft size={18} />
      </button>

      {/* Current month pill (click to open dropdown) */}
      <button
        className={`ms-pill ${dropdownOpen ? 'ms-pill--open' : ''}`}
        onClick={() => !disabled && setDropdownOpen((o) => !o)}
        disabled={disabled}
      >
        <Calendar size={14} className="ms-pill-icon" />
        <span className="ms-pill-label">{selected ? formatShort(selected) : 'Select month'}</span>
      </button>

      {/* Next arrow */}
      <button
        className="ms-arrow"
        onClick={goNext}
        disabled={!hasNext || disabled}
        title="Next month"
        aria-label="Next month"
      >
        <ChevronRight size={18} />
      </button>

      {/* Dropdown list */}
      {dropdownOpen && (
        <div className="ms-dropdown">
          {months.map((m) => (
            <button
              key={m}
              className={`ms-dropdown-item ${m === selected ? 'ms-dropdown-item--active' : ''}`}
              onClick={() => {
                onChange(m);
                setDropdownOpen(false);
              }}
            >
              {formatLong(m)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MonthSelector;
